import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { AuthBackToSignInLink } from "@/components/auth/auth-back-to-sign-in-link"
import { useAuth } from "@/hooks/use-auth"
import { API_ORIGIN } from "@/constants"
import { AnimatedTitle } from "@/components/animated-title"

interface InvitationData {
  email: string
  expires_at: string
  is_valid: boolean
  is_expired?: boolean
  is_accepted?: boolean
  organisation_name?: string | null
  user_exists?: boolean
}

interface InviteAcceptProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
}

export function InviteAccept({ className, onSuccess, ...props }: InviteAcceptProps) {
  const { key } = useParams<{ key: string }>()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null)
  const [loadingInvitation, setLoadingInvitation] = useState(true)
  const navigate = useNavigate()
  const { login } = useAuth()

  const isExistingUserOrgInvite =
    !!invitationData?.user_exists && !!invitationData?.organisation_name?.trim()

  useEffect(() => {
    if (!key) {
      setError("Invalid invitation link")
      setLoadingInvitation(false)
      return
    }

    const fetchInvitation = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/accounts/invite/${key}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        const data = await response.json()

        if (response.ok) {
          setInvitationData(data)
          setUsername(data.email)
          if (!data.is_valid) {
            if (data.is_expired) {
              setError("This invitation has expired")
            } else if (data.is_accepted) {
              setError("This invitation has already been accepted")
            } else {
              setError("This invitation is no longer valid")
            }
          }
        } else {
          setError(data.error || "Invitation not found")
        }
      } catch (fetchError) {
        console.error('Invitation fetch error:', fetchError)
        setError("Network error. Please try again.")
      } finally {
        setLoadingInvitation(false)
      }
    }

    fetchInvitation()
  }, [key])

  const acceptInvitation = async (body: Record<string, string>) => {
    if (!key) {
      setError("Invalid invitation link")
      return
    }

    setIsLoading(true)
    setError("")

    try {
      const response = await fetch(`${API_ORIGIN}/api/accounts/invite/${key}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        if (data.existing_user) {
          navigate('/login', {
            state: {
              message: data.message,
              email: invitationData?.email,
            },
          })
          onSuccess?.()
          return
        }

        if (data.access_token) {
          localStorage.setItem('auth_token', data.access_token)
          localStorage.setItem('auth_user', JSON.stringify(data.user))
          await login(username, password)
        }

        if (data.email_verification_required) {
          navigate("/register/check-email", { state: { email: username } })
        } else {
          navigate('/profile')
        }
        onSuccess?.()
      } else {
        setError(data.error || 'Failed to accept invitation')
      }
    } catch (acceptError) {
      console.error('Invitation accept error:', acceptError)
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!username || !password || !passwordConfirm) {
      setError("Please fill in all fields")
      return
    }

    if (password !== passwordConfirm) {
      setError("Passwords do not match")
      return
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long")
      return
    }

    await acceptInvitation({
      username,
      password,
      password_confirm: passwordConfirm,
    })
  }

  const handleExistingUserJoin = async () => {
    await acceptInvitation({
      username: invitationData?.email ?? "",
      password: "",
      password_confirm: "",
    })
  }

  if (loadingInvitation) {
    return (
      <div className={cn("flex flex-col items-center justify-center min-h-screen p-4", className)} {...props}>
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Loading invitation...</div>
        </div>
      </div>
    )
  }

  const orgName = invitationData?.organisation_name?.trim()

  return (
    <div className={cn("flex flex-col items-center justify-center min-h-screen p-4 bg-background", className)} {...props}>
      <div className="mb-4 md:mb-8 text-center text-6xl md:text-8xl lg:text-9xl font-bold text-foreground">
        <AnimatedTitle logoOnLightBackground />
      </div>

      <div className="w-full max-w-md">
        {invitationData && (
          <div className="mb-4 p-4 text-sm bg-muted border rounded-md">
            <div className="font-semibold">You've been invited!</div>
            {orgName ? (
              <div className="text-muted-foreground">Join <strong>{orgName}</strong></div>
            ) : null}
            <div className="text-muted-foreground">Email: {invitationData.email}</div>
          </div>
        )}
        {isExistingUserOrgInvite ? (
          <div className="flex flex-col gap-6">
            {error && (
              <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                {error}
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              You already have an account with this email. Accept the invitation to join {orgName}.
            </p>
            <Button
              type="button"
              disabled={isLoading || !invitationData?.is_valid}
              className="w-full"
              onClick={handleExistingUserJoin}
            >
              {isLoading ? "Joining…" : `Join ${orgName}`}
            </Button>
            <AuthBackToSignInLink disabled={isLoading} />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              {error && (
                <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                  {error}
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Choose a username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={isLoading || !invitationData?.is_valid}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Choose a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading || !invitationData?.is_valid}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="passwordConfirm">Confirm password</Label>
                <Input
                  id="passwordConfirm"
                  type="password"
                  placeholder="Confirm your password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  disabled={isLoading || !invitationData?.is_valid}
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading || !invitationData?.is_valid}
                className="w-full"
              >
                {isLoading
                  ? "Creating account..."
                  : orgName
                    ? `Accept invitation & join ${orgName}`
                    : "Accept invitation & create account"}
              </Button>
              <AuthBackToSignInLink disabled={isLoading} />
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
